/**
 * Google Analytics 4 tracking wrapper using dataLayer
 *
 * Privacy-safe tracking - NO personal data (email, name, etc.)
 * Only tracks anonymous events and transaction data
 *
 * Events are sent to BOTH:
 * - Marketing GTM (via dataLayer)
 * - Your own GA4 (via gtag)
 */

import { TESTER_ROLE } from './roles';

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Check if the current user is an active tester
 * Reads from localStorage auth to determine tester status
 * Checks both TESTER_ROLE and testerExpiresAt (if set)
 * Exported so it can be used for direct gtag calls
 */
export const isUserTester = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    // User data is stored in a separate 'user' key, not under 'auth'
    const userData = localStorage.getItem('user');
    if (!userData) {
      console.log('[Analytics] No user data in localStorage');
      return false;
    }

    const user = JSON.parse(userData);
    const roles = user?.roles || [];

    console.log('[Analytics] Checking tester status:', {
      hasUser: !!user,
      roles,
      testerRole: TESTER_ROLE,
      hasTesterRole: roles.includes(TESTER_ROLE),
      testerExpiresAt: user?.testerExpiresAt,
    });

    // Check if user has tester role
    if (!roles.includes(TESTER_ROLE)) {
      console.log('[Analytics] User does not have TESTER_ROLE');
      return false;
    }

    // If testerExpiresAt is null, tester access is unlimited
    if (user?.testerExpiresAt === null) {
      console.log('[Analytics] User has unlimited tester access');
      return true;
    }

    // If testerExpiresAt is undefined, no tester access
    if (user?.testerExpiresAt === undefined) {
      console.warn('[Analytics] User has TESTER_ROLE but testerExpiresAt is undefined. This indicates stale user data. User should log out and log back in, or admin should re-assign the tester role.');
      return false;
    }

    // Check if tester access has expired
    const expiresAt = new Date(user.testerExpiresAt);
    const now = new Date();
    const isActive = expiresAt > now;
    console.log('[Analytics] Tester expiration check:', {
      expiresAt: expiresAt.toISOString(),
      now: now.toISOString(),
      isActive,
    });
    return isActive;
  } catch (error) {
    console.error('[Analytics] Error checking tester status:', error);
    return false;
  }
};

/**
 * Push event to GTM's dataLayer (for marketing company)
 * Automatically adds is_tester flag to all events
 */
const pushToDataLayer = (data: Record<string, any>): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const isTester = isUserTester();
  const enrichedData = {
    ...data,
    is_tester: isTester,
    tester_mode: isTester ? 'active' : 'none',
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(enrichedData);
  console.log('[Analytics] Pushed to GTM dataLayer:', enrichedData);
};

/**
 * Send event to your own GA4 via gtag
 * Automatically adds is_tester flag to all events
 */
const sendToGtag = (eventName: string, params?: Record<string, any>): void => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  const isTester = isUserTester();
  const enrichedParams = {
    ...params,
    is_tester: isTester,
    tester_mode: isTester ? 'active' : 'none',
  };

  window.gtag('event', eventName, enrichedParams);
  console.log('[Analytics] Sent to GA4 via gtag:', eventName, enrichedParams);
};

/**
 * Track purchase completion
 * Sends to BOTH Marketing GTM and your own GA4
 * @param value - Transaction amount
 * @param currency - Currency code (e.g., 'USD', 'EUR')
 * @param method - Payment method (e.g., 'credit_card')
 */
export const trackPurchaseComplete = (
  value: number,
  currency: string = 'USD',
  method: string = 'credit_card'
): void => {
  const eventData = {
    event: 'purchase_complete',
    value,
    currency,
    method,
  };

  // Send to Marketing GTM
  pushToDataLayer(eventData);

  // Send to your own GA4
  sendToGtag('purchase', {
    value,
    currency,
    payment_type: method,
  });

  console.log('[Analytics] Tracked purchase_complete:', { value, currency, method });
};

/**
 * Track successful web registration
 * Sends to BOTH Marketing GTM and your own GA4
 * NO personal data - just the event
 */
export const trackRegistration = (): void => {
  // Send to Marketing GTM
  pushToDataLayer({
    event: 'web_registration_success',
  });

  // Send to your own GA4
  sendToGtag('sign_up', {
    method: 'web',
  });

  console.log('[Analytics] Tracked web_registration_success');
};

/**
 * Track contact interaction (email, Calendly, etc.)
 * Sends to BOTH Marketing GTM and your own GA4
 * NO personal data - just the event
 */
export const trackContact = (): void => {
  // Send to Marketing GTM
  pushToDataLayer({
    event: 'contact',
  });

  // Send to your own GA4
  sendToGtag('contact', {
    method: 'web',
  });

  console.log('[Analytics] Tracked contact');
};

/**
 * Track redesign service registration
 * Sends to BOTH Marketing GTM and your own GA4
 * NO personal data - just the event
 */
export const trackRedesignRegistration = (): void => {
  // Send to Marketing GTM
  pushToDataLayer({
    event: 'redesign_registration_success',
  });

  // Send to your own GA4
  sendToGtag('sign_up', {
    method: 'redesign_service',
  });

  console.log('[Analytics] Tracked redesign_registration_success');
};
